import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Capture the URL hash at module load — BEFORE `detectSessionInUrl` (run
// inside createClient below) consumes and clears it. A password-reset link
// arrives in implicit flow as `#access_token=…&type=recovery`, so this lets us
// route the member to the reset-password screen after the recovery session is
// established. Reading it synchronously here is race-free; the async session
// parsing only happens after the client is constructed.
const initialHash = typeof window !== 'undefined' ? window.location.hash : ''

/** True when this page load arrived via a Supabase password-recovery link. */
export const wasPasswordRecoveryLink = (): boolean =>
  /(?:^|[#&])type=recovery(?:&|$)/.test(initialHash)

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
      'Create frontend/.env.local with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY. ' +
      'See frontend/.env.example.'
  )
}

/**
 * Browser-side Supabase client. Uses the publishable anon key only —
 * never the service role key. RLS policies in Postgres enforce who can
 * read/write what.
 *
 * flowType MUST be 'implicit' for the 6-digit OTP code entry flow on
 * /signup to work. In PKCE mode, signInWithOtp stores a hashed PKCE
 * token (token_type='recovery_token', token_hash prefixed with 'pkce_')
 * that is NOT verifiable by verifyOtp({type:'email', token:'<6 digits>'}),
 * causing every verification attempt to return "token has expired or is
 * invalid" within seconds of issuance. Implicit flow stores the raw OTP
 * in a form verifyOtp can match.
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'implicit',
  },
})
